import { nextTick } from "vue";
import { settleConnected } from "#ce/apps/shared/composables/settle-connected";
import { whenI18nReady } from "#ce/components/SpFinder/i18n";
import { useHostElement } from "#ce/composables/useHostElement";
import { isRegisteredTag } from "#ce/framework/registration";
import { whenComponentsI18nReady } from "#ce/shared/components-i18n";

const SETTLE_PASSES = 3;

/**
 * Returns `whenRendered`, which resolves once the result view has drawn its current payload: the
 * finder strings have loaded, every catalog element it renders has mounted, failed or left, and the
 * component strings its cards read have loaded. Elements a newer render adds meanwhile are waited
 * for too.
 *
 * @returns {{ whenRendered: () => Promise<void> }} resolves after at most `SETTLE_PASSES` (3) passes, without waiting for elements added later
 */
export function useRenderedResults() {
  const host = useHostElement();

  function catalogElements() {
    return [...(host?.shadowRoot?.querySelectorAll("*") ?? [])].filter((element) => isRegisteredTag(element.localName));
  }

  async function whenRendered() {
    await whenI18nReady;
    await nextTick();

    const settled = new Set();

    for (let pass = 0; pass < SETTLE_PASSES; pass++) {
      const elements = catalogElements().filter((element) => !settled.has(element));

      if (!elements.length) {
        break;
      }

      elements.forEach((element) => settled.add(element));
      await Promise.all(elements.map(settleConnected));
      await whenComponentsI18nReady;
      await nextTick();
    }
  }

  return { whenRendered };
}
